// Boot: build the faceplate, wire it to the engine, and gate audio behind the
// start overlay (autoplay policy). All sound stays behind the gesture.

import './ui/theme.css';
import { AudioEngine, AudioWorkletUnsupportedError } from './engine';
import { buildFaceplate } from './ui/layout';
import { initBindings } from './ui/bindings';
import { initPresetBar } from './ui/preset-bar';

const engine = new AudioEngine();

function boot(): void {
  const overlay = document.querySelector<HTMLDivElement>('#start-overlay');
  const startBtn = document.querySelector<HTMLButtonElement>('#start-audio');
  const root = document.querySelector<HTMLElement>('#synth-root');

  if (!overlay || !startBtn || !root) {
    console.error('ClaudeSynth: required DOM nodes missing');
    return;
  }

  buildFaceplate(root);
  const bindings = initBindings(engine, root);
  initPresetBar(engine, document);

  startBtn.addEventListener('click', async () => {
    try {
      await engine.resume();
    } catch (err) {
      if (err instanceof AudioWorkletUnsupportedError) {
        overlay.innerHTML =
          '<p class="fatal">Your browser does not support AudioWorklet, ' +
          'which this synth requires. Please try a recent version of Chrome, ' +
          'Edge, Safari, or Firefox.</p>';
        return;
      }
      throw err;
    }
    await bindings.onStarted();
    overlay.hidden = true;
  });

  // Autoplay/visibility: suspend audio when the tab is hidden, resume when shown
  // (only once the context exists, i.e. after the start gesture).
  document.addEventListener('visibilitychange', () => {
    if (!engine.isInitialized) return;
    if (document.hidden) void engine.suspend();
    else void engine.resume();
  });

  (globalThis as Record<string, unknown>).synth = engine;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

// PWA: register the offline service worker in production builds only.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').catch(() => {
      /* offline support is optional */
    });
  });
}
